import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Nav, Table } from 'react-bootstrap';
import { getMyFastrackNodes } from '../../api/fastrackApi';
import { fmtIST } from '../../utils/dateFormat';

const accent = '#cc66ff';
const cardDark = { background: 'linear-gradient(135deg, #0d0d0d 0%, #1a0033 100%)', color: '#fff', border: '1px solid #9933ff44' };

const BonusTable = ({ rows }) => {
  if (rows.length === 0) return (
    <div style={{ color: '#555', fontSize: 13, padding: '24px 0' }}>No completed cycles yet.</div>
  );

  return (
    <Card style={cardDark}>
      <Table responsive className="mb-0" style={{ color: '#ddd' }}>
        <thead style={{ borderBottom: '1px solid #9933ff44' }}>
          <tr>
            <th style={{ color: '#aaa', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>#</th>
            <th style={{ color: '#aaa', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>Slot</th>
            <th style={{ color: '#aaa', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>Completed</th>
            <th style={{ color: '#aaa', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>Bonus (20%)</th>
            <th style={{ color: '#aaa', fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row._id} style={{ borderBottom: '1px solid #9933ff22' }}>
              <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{row.cycleNumber ?? i + 1}</td>
              <td style={{ fontSize: 13, color: accent, whiteSpace: 'nowrap' }}>{row.planName}</td>
              <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtIST(row.completedAt)}</td>
              <td style={{ fontSize: 13, color: '#66ff99', fontWeight: 600, whiteSpace: 'nowrap' }}>{row.bonusAmount} USDT</td>
              <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                {row.bonusStatus === 'credited' && <span style={{ color: '#66ff99', fontWeight: 600 }}>✓ Credited</span>}
                {row.bonusStatus === 'held' && <span style={{ color: '#ffaa44', fontWeight: 600 }}>⏸ On Hold</span>}
                {row.bonusStatus === 'pending' && <span style={{ color: '#555' }}>—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Card>
  );
};

const Fastrack = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePlanKey, setActivePlanKey] = useState('all');

  useEffect(() => {
    getMyFastrackNodes()
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" style={{ color: accent }} />
      </Container>
    );
  }

  const hasPlans = data?.plans?.length > 0;

  // Build rows for the active tab (nodes live inside each plan's renewals[])
  const allRows = (data?.plans || []).flatMap(({ plan, renewals }) =>
    (renewals || []).flatMap(({ nodes }) =>
      (nodes || [])
        .filter((n) => n.status === 'complete')
        .map((n) => ({ ...n, planName: plan.planName }))
    )
  );

  const visibleRows = activePlanKey === 'all'
    ? allRows
    : (data?.plans || [])
        .filter((pd) => pd.plan._id === activePlanKey)
        .flatMap(({ plan, renewals }) =>
          (renewals || []).flatMap(({ nodes }) =>
            (nodes || []).filter((n) => n.status === 'complete').map((n) => ({ ...n, planName: plan.planName }))
          )
        );

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4">
        <div className="mb-4">
          <h4 style={{ color: '#fff', fontWeight: 700 }}>Global Fastrack Bonus</h4>
          <div style={{ color: '#aaa', fontSize: 13 }}>
            Global fastrack income — earn 20% when your Cycle completes, then rebirth and repeat
          </div>
        </div>

        {data?.summary && (
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <Card style={{ ...cardDark, borderColor: '#66ff9944' }} className="text-center p-3">
                <div style={{ fontSize: 24, fontWeight: 800, color: 'rgb(0, 229, 255)' }}>
                  {data.summary.totalBonusEarned.toFixed(2)}
                </div>
                <div style={{ color: '#aaa', fontSize: 12 }}>Total Credited (USDT)</div>
              </Card>
            </Col>
          </Row>
        )}

        {!hasPlans ? (
          <Card style={cardDark} className="text-center p-5">
            <div style={{ color: '#555', fontSize: 14 }}>
              You have not subscribed to any plan yet.<br />
              Subscribe to a plan to enter the Global Fastrack network.
            </div>
          </Card>
        ) : (
          <>
            <Nav variant="tabs" className="mb-4" style={{ borderBottom: '1px solid #9933ff44', overflowX: 'auto', overflowY: 'hidden', flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch' }}>
              <Nav.Item>
                <Nav.Link
                  active={activePlanKey === 'all'}
                  onClick={() => setActivePlanKey('all')}
                  style={{
                    color: activePlanKey === 'all' ? accent : '#aaa',
                    background: activePlanKey === 'all' ? '#1a003388' : 'transparent',
                    border: activePlanKey === 'all' ? `1px solid ${accent}` : '1px solid transparent',
                    borderBottom: 'none',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  All
                </Nav.Link>
              </Nav.Item>
              {data.plans.map(({ plan }) => (
                <Nav.Item key={plan._id}>
                  <Nav.Link
                    active={activePlanKey === plan._id}
                    onClick={() => setActivePlanKey(plan._id)}
                    style={{
                      color: activePlanKey === plan._id ? accent : '#aaa',
                      background: activePlanKey === plan._id ? '#1a003388' : 'transparent',
                      border: activePlanKey === plan._id ? `1px solid ${accent}` : '1px solid transparent',
                      borderBottom: 'none',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {plan.planName}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>

            <h6 style={{ color: '#aaa', margin: '0 0 12px' }}>Bonus Transaction Log</h6>
            <BonusTable rows={visibleRows} />
          </>
        )}
      </Container>
    </div>
  );
};

export default Fastrack;
